function pad2(n) {
  return String(n).padStart(2, '0');
}

/** `dd.MM.yyyy HH.mm.ss` (24h), e.g. `08.05.2026 19.14.03` */
export function formatCareDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const date = `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())}`;
  const time = `${pad2(d.getHours())}.${pad2(d.getMinutes())}.${pad2(d.getSeconds())}`;
  return `${date} ${time}`;
}

export function jsonPreview(value, max = 96) {
  if (value == null || value === '') return "";
  try {
    const s = typeof value === 'string' ? value : JSON.stringify(value);
    if (s.length <= max) return s;
    return `${s.slice(0, max)}…`;
  } catch {
    return "";
  }
}

/** UsersAuth relation: one-line summary (tooltips / fallbacks) */
export function formatRelationUser(user) {
  if (user == null) return "";
  if (typeof user === 'string') return user;
  const parts = [user.name, user.email, user.role].filter(Boolean);
  return parts.length ? parts.join(' · ') : user.id || "";
}

/** Relation value shown as a single display name (embedded UsersAuth object or id string). */
export function formatRelationUserName(user) {
  if (user == null || user === '') return "";
  if (typeof user === 'string') return "";
  const n = user.name;
  if (n != null && String(n).trim() !== '') return String(n).trim();
  const email = user.email;
  if (email != null && String(email).trim() !== '') return String(email).trim();
  const username = user.username;
  if (username != null && String(username).trim() !== '') return String(username).trim();
  return user.id || "";
}

export function strSorter(dataIndex) {
  return (a, b) =>
    String(a?.[dataIndex] ?? '').localeCompare(String(b?.[dataIndex] ?? ''));
}

export function dateSorter(dataIndex) {
  return (a, b) =>
    new Date(a?.[dataIndex] || 0).getTime() - new Date(b?.[dataIndex] || 0).getTime();
}

export function numSorter(dataIndex) {
  return (a, b) =>
    (Number(a?.[dataIndex]) || 0) - (Number(b?.[dataIndex]) || 0);
}

export function selectFilters(values) {
  return values.map((v) => ({ text: v, value: v }));
}

const OPENING_HOURS_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const OPENING_HOURS_DAY_LABEL = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

/** @returns {Record<string, unknown>|null} */
export function coerceOpeningHoursRecord(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      /* not JSON */
    }
  }
  return null;
}

/** True when `rec` looks like `{ mon?, tue?, … }` opening-hours map. */
export function isOpeningHoursShape(rec) {
  if (!rec || typeof rec !== 'object' || Array.isArray(rec)) return false;
  return OPENING_HOURS_DAY_KEYS.some((k) => Object.prototype.hasOwnProperty.call(rec, k));
}

function prettifyHourRange(raw) {
  const s = String(raw).trim();
  if (!s) return '';
  const parts = s.split('-').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 2) return `${parts[0]}–${parts[1]}`;
  return s;
}

function daySlotFromRecord(rec, key) {
  const raw = rec[key];
  const str =
    raw == null || raw === ''
      ? ''
      : typeof raw === 'string'
        ? raw.trim()
        : String(raw).trim();
  if (!str) return { closed: true, label: '' };
  return { closed: false, label: prettifyHourRange(str) };
}

function formatDayRangeLabel(startKey, endKey) {
  const a = OPENING_HOURS_DAY_LABEL[startKey];
  const b = OPENING_HOURS_DAY_LABEL[endKey];
  if (!a || !b) return '';
  return startKey === endKey ? a : `${a}–${b}`;
}

/**
 * Compact one-line summary + multi-line detail for tooltips.
 * @param {Record<string, unknown>} rec
 * @returns {{ summary: string, detail: string }}
 */
export function summarizeOpeningHours(rec) {
  const days = OPENING_HOURS_DAY_KEYS.map((key) => ({
    key,
    short: OPENING_HOURS_DAY_LABEL[key],
    ...daySlotFromRecord(rec, key),
  }));

  const detail = days
    .map((d) => {
      const hours = d.closed ? 'Closed' : d.label || "";
      return `${d.short}: ${hours}`;
    })
    .join('\n');

  const runs = [];
  let i = 0;
  while (i < days.length) {
    const start = i;
    const cur = days[i];
    const token = cur.closed ? '\0closed' : cur.label;
    i += 1;
    while (i < days.length) {
      const next = days[i];
      const nextToken = next.closed ? '\0closed' : next.label;
      if (nextToken !== token) break;
      i += 1;
    }
    const endIdx = i - 1;
    runs.push({
      startKey: days[start].key,
      endKey: days[endIdx].key,
      closed: token === '\0closed',
      hours: token === '\0closed' ? '' : String(token),
    });
  }

  const summaryParts = runs.map((run) => {
    const range = formatDayRangeLabel(run.startKey, run.endKey);
    if (run.closed) return `${range} closed`;
    return `${range} ${run.hours}`;
  });

  const summary = summaryParts.length ? summaryParts.join(' · ') : "";

  return { summary, detail };
}

/** @returns {unknown[]|null} */
export function coerceClosingDaysList(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * @param {unknown[]} list
 * @returns {{ summary: string, tooltip: string }}
 */
export function summarizeClosingDays(list) {
  const indices = new Set();
  for (const item of list) {
    const k = String(item ?? '')
      .trim()
      .toLowerCase();
    if (OPENING_HOURS_DAY_KEYS.includes(k)) indices.add(k);
  }

  const orderedKeys = OPENING_HOURS_DAY_KEYS.filter((k) => indices.has(k));
  if (orderedKeys.length === 0) {
    return { summary: "", tooltip: '' };
  }

  const runs = [];
  let runStart = orderedKeys[0];
  let prevIdx = OPENING_HOURS_DAY_KEYS.indexOf(orderedKeys[0]);

  for (let i = 1; i < orderedKeys.length; i += 1) {
    const key = orderedKeys[i];
    const idx = OPENING_HOURS_DAY_KEYS.indexOf(key);
    if (idx === prevIdx + 1) {
      prevIdx = idx;
    } else {
      runs.push({
        start: runStart,
        end: OPENING_HOURS_DAY_KEYS[prevIdx],
      });
      runStart = key;
      prevIdx = idx;
    }
  }
  runs.push({
    start: runStart,
    end: OPENING_HOURS_DAY_KEYS[prevIdx],
  });

  const summary = runs
    .map((run) => formatDayRangeLabel(run.start, run.end))
    .join(' · ');

  const tooltip =
    orderedKeys.length > 1
      ? orderedKeys.map((k) => OPENING_HOURS_DAY_LABEL[k]).join('\n')
      : '';

  return { summary, tooltip };
}

/** JSON array string, array of values, or single slug string. */
export function coerceStringArray(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* treat as single token */
    }
    const t = value.trim();
    return t === '' ? null : [t];
  }
  return null;
}

/** Human-readable label from API slug (e.g. `heart_disease` → Heart disease). */
export function formatConcernLabel(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const spaced = s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return spaced
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Product line labels: keep human-readable strings as-is; prettify slug-like tokens. */
export function formatProductLabel(raw) {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    const pick = raw.name ?? raw.title ?? raw.label ?? raw.product_name ?? raw.store_product;
    return pick != null ? formatProductLabel(pick) : '';
  }
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/[_-]/.test(s)) return formatConcernLabel(s);
  return s;
}
